import './ResourceTabs.scss';
import { useState, useEffect } from 'react';
import { useLocalStorage, useTokenProvider, useDealerInfo, useConfig, useBreakpoint } from '@gplibraries/react/hooks';
import { Icon, LoadingCircle } from '@gplibraries/react/components';
import ResourceTabsBar from './ResourceTabsBar.jsx';
import ResourceTabsContent from './ResourceTabsContent.jsx';
import ResourceTabsSearchResults from './ResourceTabsSearchResults.jsx';
import ResourceTabsMobile from './ResourceTabsMobile.jsx';
import { useQuery } from '@tanstack/react-query';
import { fetchResources } from '@gplibraries/react/api';

export default function resourceTabs(props) {

  const { type } = props;

  const { data: dealerInfo, loading: dealerInfoLoading } = useDealerInfo();
  const [searchQuery, setSearchQuery] = useState('');
  const [resources, setResources] = useState(null);
  const [activeTab, setActiveTab] = useLocalStorage(`lpda_active_tab_${type}`, '');
  const isMobile = useBreakpoint();
  const { dealerAccessApiScope, dealerAccessApiHost } = useConfig();
  const getToken = useTokenProvider({ scopes: [dealerAccessApiScope]});

  const { isLoading: loading, error, data } = useQuery({
    enabled: Boolean(getToken && dealerAccessApiHost),
    queryKey: ['resources', type],
    queryFn: () => getToken()
      .then((resolvedToken) => fetchResources(dealerAccessApiHost, resolvedToken.accessToken, type))
      .then(res => res.json())
  })

  useEffect(() => {
    // set the first tab to active by default
    if (resources?.data && !activeTab) {
      // get unique parent category IDs from the resources returned
      const categoryIDs = [...new Set(resources.data.map(r => r.category).flat()?.map(c => c?.parent?.id)?.filter(x => Boolean(x)))]
      setActiveTab(categoryIDs[0]);
    }
  }, [resources]);

  const handleSearchQuery = (e) => {
    if (!e.target.value) {
      setActiveTab(resources.meta.categories
        .sort((a, b) => a.weight - b.weight)
        .filter(cat => !cat.parent)[0].id);
    }
    setSearchQuery(e.target.value);
  }

  useEffect(() => {

    if (data && !dealerInfoLoading) {

      // if the user is not acting as a dealer, show all resources
      if (!dealerInfo || type === 'tm') {
        setResources(data);
      } else {

        let filtered = data?.data.filter(resource => {

          // If the locality array only contains either domestic or export, show
          // only to those dealers.
          if (
            (resource.field_locality?.every(l => l === 'domestic') && dealerInfo.territory.slice(0,3) >= 900) ||
            (resource.field_locality?.every(l => l === 'export') && dealerInfo.territory.slice(0,3) <= 900)
          ) return false;

          if (
            resource.field_hide_from_specific_dealers.includes(dealerInfo.dealer_number) ||
            resource.field_hide_from_trty.includes(dealerInfo.territory.slice(0,3))
          ) return false;

          if (
            resource.field_show_only_specific_dealers.length > 0 &&
            !resource.field_show_only_specific_dealers.includes(dealerInfo.dealer_number)
          ) return false;

          if (
            resource.field_show_to_trty.length > 0 &&
            !resource.field_show_to_trty.includes(dealerInfo.territory.slice(0,3))
          ) return false;

          return true;

        });

        setResources({ ...data, data: filtered })

      }

    }
  }, [data, dealerInfoLoading]);

  useEffect(() => {
    if (resources) {
      // handle changing of active tab if searching
      if (searchQuery.length > 0 && activeTab !== 'search') {
        setActiveTab('search')
      } else if (searchQuery.length < 1 && activeTab === 'search') {
        setActiveTab(resources.meta.categories.filter(category => category.parent === null)[0].id);
      }
    }
  }, [searchQuery, resources]);

  // filter resources based on selected category or search query.
  const filterResources = (resources) => activeTab === 'search'
    ? resources.filter(r => r.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : resources.filter(r => r?.category?.some(cat => cat.parent.id === activeTab))

  // Get list of subcategories for selected category
  const filterActiveSubcategories = (subcat) => subcat
    .filter(cat => cat.parent?.id === activeTab)

  if (error) throw error
  if (loading) return <LoadingCircle className={'large color'}/>

  return (
    <div className={'resources-container flex-col full'}>

      {(resources?.data?.length > 0 && !dealerInfoLoading) && <>

        {/* Search bar */}
        <div className="input-wrapper resource-search">
          <Icon glyph={'search'}/>
          <input type="search" placeholder={'What can we help you find?'} onChange={handleSearchQuery}/>
        </div>

        <div className="flex-col full stretch no-gap">

          {!isMobile ? (<>
            <ResourceTabsBar
              categories={resources.meta.categories.filter(c => c?.parent === null)}
              searchQuery={searchQuery}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />

            {/* If there is a search query, show list of resources that match
            along with their parent category. */}
            {searchQuery ? (
              <ResourceTabsSearchResults
                resources={filterResources(resources?.data ?? [])}
                categories={resources?.meta?.categories ?? []}
              />
            ) : (
              <ResourceTabsContent
                resources={filterResources(resources?.data ?? [])}
                resourceSubcategories={filterActiveSubcategories(resources?.meta?.categories ?? [])}
                activeTab={activeTab}
              />
            )}

          </>) : (
            <ResourceTabsMobile
              resources={resources?.data ?? []}
              categories={resources.meta.categories}
              searchQuery={searchQuery}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          )}

        </div>

      </>}

      {resources?.data?.length === 0 &&

        <div className={'flex-row center full'}>
          <div className={'message'}>Sorry, no resources are available at this
            time.
          </div>
        </div>

      }

    </div>
  );
}